/**
 * PATH: packages/ui/src/components/settings/organisation-tab/QuoteBuilderModeSelector.tsx
 * FILE_ID: UI-SETTINGS-QBMODE-001
 * PURPOSE: Trade-mode dropdown for the Organisation settings tab. Lets
 *          org admins flip between waterproofing / steel / custom; the
 *          Quote Hub (`apps/quote/`) reads the resulting value to gate
 *          steel-fabrication-only UI (the 8 allowance bucket columns).
 *
 * WHY: A waterproofing company never uses steel-company columns and
 *      vice versa — different trades, different productivity rates,
 *      different allowance categories. Before this MR landed, the
 *      Bravada Quote Hub had no trade switch and bolted the steel
 *      allowance buckets onto every quote regardless of tenant
 *      (commit 81823c119). This selector is the user-facing half of
 *      the fix; `Organisation.quote_builder_mode` (Django) is the
 *      storage half.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌─────────────────────────┐
 *   │ organisation     │ -> │ select onChange ->      │
 *   │ .quote_builder_  │    │ onSelect(nextMode) ->   │
 *   │ mode             │    │ parent persists via     │
 *   │                  │    │ /organisations/<id>/    │
 *   │                  │    │ settings/ PATCH         │
 *   └──────────────────┘    └─────────────────────────┘
 *
 * RELATED:
 *   - UI-SETTINGS-TYPES-001 — QuoteBuilderModeValue literal
 *   - QB-LIB-TRADEMODE-001  — Quote Hub's local copy of the contract
 *   - organisation/models/core.py::QUOTE_BUILDER_MODE_CHOICES (backend)
 */

import { Layers } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { QuoteBuilderModeValue } from './organisation-tab-types'

/**
 * Mode options shown in the dropdown. Order matters: 'waterproofing'
 * is FIRST so the default selection at the top of an empty list
 * matches the backend default for new tenants. Description copy is
 * sized to fit one line on the settings modal (~480px wide).
 */
interface ModeOption {
  value: QuoteBuilderModeValue
  label: string
  description: string
}

const MODE_OPTIONS: readonly ModeOption[] = [
  {
    value: 'waterproofing',
    label: 'Waterproofing',
    description:
      'Hides steel-fabrication allowance columns (Gal, Prime, Paint, Lift, Transport, Fasteners, Polish, Misc).',
  },
  {
    value: 'steel',
    label: 'Steel Fabrication',
    description:
      'Shows the 8 steel-fabrication allowance bucket columns by default.',
  },
  {
    value: 'custom',
    label: 'Custom',
    description:
      'Shows every available column. Use this if your trade does not match either preset.',
  },
] as const

interface Props {
  /**
   * Current mode for this organisation. Undefined means "not yet
   * loaded" — selector still renders with 'waterproofing' selected
   * (the backend default) to avoid a flash of empty UI.
   */
  value: QuoteBuilderModeValue | undefined
  /** Notified when the user picks a new mode. */
  onSelect: (next: QuoteBuilderModeValue) => void
  /** Disabled state for non-admins / save-in-flight. */
  disabled?: boolean
}

/**
 * Find the option description for the currently-selected mode.
 * Pure I→O. Defaults to the waterproofing description when value is
 * unrecognised so the help-text never goes blank. Kept unexported
 * (used only by the component below) so react-refresh's fast-refresh
 * boundary stays clean — a file that mixes components and exported
 * non-components blocks HMR for this whole module.
 */
function describeMode(value: QuoteBuilderModeValue | undefined): string {
  const opt = MODE_OPTIONS.find((m) => m.value === value)
  return (opt ?? MODE_OPTIONS[0]).description
}

export function QuoteBuilderModeSelector({ value, onSelect, disabled }: Props) {
  const effectiveValue: QuoteBuilderModeValue = value ?? 'waterproofing'

  return (
    <div className="space-y-3 pt-6 border-t border-slate-200 dark:border-slate-800">
      <div className="flex items-start gap-3">
        <Layers className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <label
            htmlFor="organisation-quote-builder-mode"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Quote Builder Mode
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Set at onboarding. Drives which columns appear in the Quote Hub
            by default for everyone in this organisation.
          </p>
        </div>
      </div>

      <select
        id="organisation-quote-builder-mode"
        value={effectiveValue}
        disabled={disabled}
        onChange={(e) => onSelect(e.target.value as QuoteBuilderModeValue)}
        className={cn(
          'w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900',
          'border-slate-200 dark:border-slate-700 text-sm',
          'text-slate-900 dark:text-slate-100',
          'focus:outline-none focus:ring-2 focus:ring-teal-500',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        {MODE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        {describeMode(effectiveValue)}
      </p>
    </div>
  )
}

export default QuoteBuilderModeSelector
